import {createStyles, Group, Stack, Table, Text} from '@mantine/core';
import React, {useMemo} from 'react';

import {StructuredTableKind} from 'notebook/structured-tables/StructuredTableKind';
import kindToIcon from 'notebook/structured-tables/util/kindToIcon';
import type {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {TABLE_NAME_BY_COMPONENT_NAME} from 'procedures/planning/ProcedureExperimentConstants';
import {ExperimentalInputsComponentName} from 'procedures/planning/ProcedurePlanTypes';
import type {ProcedureParameterDataTypeDefinition} from 'procedures/ProcedureDomainTypes';
import type {UnitSummary} from 'procedures/shared-components/schema-field-type-selectors/procedureSchemaFieldConstants';
import {isItemLinkFieldType} from 'tag-schema/util/TagSchemaUtil';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {
  CellColorGrade,
  computeDataParameterCellColorGrade,
  computeEquipmentCellColorGrade,
  computeMaterialInputCellColorGrade,
  type EquipmentValue,
  hasMultiplePlannedValues,
  type MaterialInputValue,
} from './PMEICellColorGrade';
import {
  computeDataParameterCellTooltip,
  computeEquipmentCellTooltip,
  computeMaterialInputCellTooltip,
  type CellTooltipContent,
  type ColumnKind,
} from './PMEICellTooltipContent';
import type {LinkedPMEI} from './PMEIMaterialLinkUtils';
import PMEIParameterCellTooltip from './PMEIParameterCellTooltip';
import type {MinMaxTypedValue} from './PMEIParameterRange';
import {
  buildCell,
  buildMaterialInputCell,
  renderMaterialLinkCell,
  UnrecordedValue,
} from './PMEIValueRenderer';
import type {PMEIParameterData} from './usePMEISidepanelDetail';

/**
 * The content of a Confirmed or Measured cell: the unrecorded placeholder when the parameter has no
 * value records at all in that column, otherwise the rendered values.
 *
 * `recordCount` counts value records rather than rendered values on purpose. A record that renders
 * as nothing — a blank entry, or a material input carrying only an amount or only a material — still
 * means the column was filled in, so it keeps its rendered content instead of claiming nothing was
 * recorded.
 */
function cellContentOrPlaceholder({
  recordCount,
  columnKind,
  worksheetId,
  recordedContent,
}: {
  recordCount: number;
  columnKind: ColumnKind;
  worksheetId: string | null;
  recordedContent: React.ReactNode;
}): React.ReactNode {
  if (recordCount === 0) {
    return <UnrecordedValue columnKind={columnKind} worksheetId={worksheetId} />;
  }
  return recordedContent;
}

/**
 * Builds a Confirmed or Measured data-parameter cell with its color grade and explanatory tooltip.
 * When a tooltip is present, the values HoverCard is disabled (`disableReadingsHoverCard`) so the
 * two overlays never collide.
 *
 * A parameter that plans more than one value is not evaluated against plan at all: no color grade, no
 * tooltip, and no out-of-range bolding. The range is withheld from the renderer to achieve the last
 * of those, since bolding is applied per value inside `PMEIValueRenderer` and would otherwise flag a
 * confirmed value that nothing else in the row flags.
 */
function buildDataParameterCell({
  parameterName,
  columnKind,
  cellValues,
  plannedValues,
  dataType,
  minimum,
  maximum,
  worksheetId,
}: {
  parameterName: string;
  columnKind: ColumnKind;
  cellValues: Array<TypedValue>;
  plannedValues: Array<TypedValue>;
  dataType: ProcedureParameterDataTypeDefinition;
  minimum: MinMaxTypedValue;
  maximum: MinMaxTypedValue;
  worksheetId: string | null;
}): ParameterCell {
  const isEvaluatedAgainstPlan = !hasMultiplePlannedValues(plannedValues);
  const effectiveMinimum = isEvaluatedAgainstPlan ? minimum : null;
  const effectiveMaximum = isEvaluatedAgainstPlan ? maximum : null;
  const grade = computeDataParameterCellColorGrade(
    cellValues,
    plannedValues,
    effectiveMinimum,
    effectiveMaximum
  );
  const tooltip = computeDataParameterCellTooltip({
    parameterName,
    columnKind,
    cellValues,
    plannedValues,
    minimum: effectiveMinimum,
    maximum: effectiveMaximum,
  });
  const hasDifferences = tooltip != null;
  return {
    content: cellContentOrPlaceholder({
      recordCount: cellValues.length,
      columnKind,
      worksheetId,
      recordedContent: buildCell({
        values: cellValues,
        dataType,
        minimum: effectiveMinimum,
        maximum: effectiveMaximum,
        disableReadingsHoverCard: hasDifferences,
      }),
    }),
    grade,
    ...(tooltip != null ? {tooltip} : {}),
  };
}

/**
 * Builds an equipment Confirmed cell with its color grade and differences tooltip. When a tooltip is
 * present the values HoverCard is disabled (`disableReadingsHoverCard`) so the two overlays never
 * collide.
 */
function buildEquipmentConfirmedCell({
  parameterName,
  cellValues,
  plannedValues,
  dataType,
  worksheetId,
}: {
  parameterName: string;
  cellValues: Array<EquipmentValue>;
  plannedValues: Array<EquipmentValue>;
  dataType: ProcedureParameterDataTypeDefinition;
  worksheetId: string | null;
}): ParameterCell {
  const isEntity = isItemLinkFieldType(dataType.type);
  const grade = computeEquipmentCellColorGrade(parameterName, cellValues, plannedValues, isEntity);
  const tooltip = computeEquipmentCellTooltip({parameterName, cellValues, plannedValues, isEntity});
  const hasDifferences = tooltip != null;
  return {
    content: cellContentOrPlaceholder({
      recordCount: cellValues.length,
      columnKind: 'confirmed',
      worksheetId,
      recordedContent: buildCell({
        values: cellValues.map((v) => v.equipment),
        dataType,
        disableReadingsHoverCard: hasDifferences,
      }),
    }),
    grade,
    ...(tooltip != null ? {tooltip} : {}),
  };
}

/**
 * Builds a material-input Confirmed cell with its color grade and differences tooltip. When a
 * tooltip is present the values HoverCard is disabled (`disableReadingsHoverCard`) so the two
 * overlays never collide.
 */
function buildMaterialInputConfirmedCell({
  parameterName,
  cellValues,
  plannedValues,
  dataType,
  amountUnit,
  worksheetId,
}: {
  parameterName: string;
  cellValues: Array<MaterialInputValue>;
  plannedValues: Array<MaterialInputValue>;
  dataType: ProcedureParameterDataTypeDefinition;
  amountUnit: UnitSummary | null;
  worksheetId: string | null;
}): ParameterCell {
  const isEntity = isItemLinkFieldType(dataType.type);
  const grade = computeMaterialInputCellColorGrade(cellValues, plannedValues, isEntity);
  const tooltip = computeMaterialInputCellTooltip({
    parameterName,
    cellValues,
    plannedValues,
    isEntity,
    amountUnit,
  });
  const hasDifferences = tooltip != null;
  return {
    content: cellContentOrPlaceholder({
      recordCount: cellValues.length,
      columnKind: 'confirmed',
      worksheetId,
      recordedContent: buildMaterialInputCell(cellValues, dataType, amountUnit, hasDifferences),
    }),
    grade,
    ...(tooltip != null ? {tooltip} : {}),
  };
}

/**
 * Renders the four parameter sections (data parameters, material inputs,
 * material outputs, equipment) as static table cards in the PMEI side panel.
 */

const SECTION_CONFIG = [
  {
    key: 'parameters',
    title: TABLE_NAME_BY_COMPONENT_NAME[ExperimentalInputsComponentName.PARAMETERS],
    icon: kindToIcon(StructuredTableKind.PROCEDURE_DATA_PARAMETERS_CONFIG),
  },
  {
    key: 'materialInputs',
    title: TABLE_NAME_BY_COMPONENT_NAME[ExperimentalInputsComponentName.MATERIAL_INPUTS],
    icon: kindToIcon(StructuredTableKind.PROCEDURE_MATERIAL_INPUTS),
  },
  {
    key: 'materialOutputs',
    title: 'Material outputs',
    icon: kindToIcon(StructuredTableKind.PROCEDURE_MATERIAL_OUTPUTS),
  },
  {
    key: 'equipment',
    title: TABLE_NAME_BY_COMPONENT_NAME[ExperimentalInputsComponentName.EQUIPMENT],
    icon: kindToIcon(StructuredTableKind.PROCEDURE_EQUIPMENT),
  },
] as const;

interface ParameterCell {
  content: React.ReactNode;
  grade?: CellColorGrade;
  tooltip?: CellTooltipContent;
}

interface ParameterSectionProps {
  sectionKey: string;
  title: string;
  icon: BenchlingIconName;
  columns: Array<string>;
  rows: Array<{key: string; cells: Array<ParameterCell>}>;
}

const ParameterSection = React.memo(
  ({sectionKey, title, icon, columns, rows}: ParameterSectionProps): JSX.Element | null => {
    const {classes} = useStyles();

    if (rows.length === 0) {
      return null;
    }

    const gradeClass: Partial<Record<CellColorGrade, string>> = {
      [CellColorGrade.UNPLANNED]: classes.cellUnplanned,
      [CellColorGrade.MISMATCH]: classes.cellMismatch,
      [CellColorGrade.OUT_OF_RANGE]: classes.cellOutOfRange,
      [CellColorGrade.STRICT_MISMATCH]: classes.cellOutOfRange,
    };

    return (
      <div
        className={classes.sectionCard}
        {...getTestProps({component: 'PMEIParameterTables', element: `section-${sectionKey}`})}
      >
        <Group spacing="xs" className={classes.sectionTitle}>
          <BenchlingIcon icon={icon} />
          <Text fw={600} fz="sm">
            {title}
          </Text>
        </Group>
        <Table className={classes.table} horizontalSpacing="md">
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                {row.cells.map((cell, i) => {
                  const className = cell.grade != null ? gradeClass[cell.grade] : undefined;
                  return (
                    <td
                      key={i}
                      className={className}
                      {...(className != null
                        ? getTestProps({
                            component: 'PMEIParameterTables',
                            element: `cell-grade-${cell.grade}`,
                          })
                        : {})}
                    >
                      {cell.tooltip != null ? (
                        <PMEIParameterCellTooltip tooltip={cell.tooltip}>
                          {cell.content}
                        </PMEIParameterCellTooltip>
                      ) : (
                        cell.content
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }
);

export default function PMEIParameterTables({
  parameterData,
  downstreamIndex,
  originIndex,
  onNavigateToPmei,
  worksheetId,
}: {
  parameterData: PMEIParameterData;
  downstreamIndex: Map<string, Array<LinkedPMEI>> | null;
  originIndex: Map<string, Array<LinkedPMEI>> | null;
  onNavigateToPmei: ((pmeiId: string) => void) | null;
  /** Destination for the unrecorded-value placeholders; null when no worksheet is accessible. */
  worksheetId: string | null;
}): JSX.Element {
  const sections = useMemo(
    (): Array<{
      config: (typeof SECTION_CONFIG)[number];
      columns: Array<string>;
      rows: Array<{key: string; cells: Array<ParameterCell>}>;
    }> => [
      {
        config: SECTION_CONFIG[0],
        columns: ['Parameter', 'Setpoint', 'Confirmed', 'Measured'],
        rows: parameterData.dataParameters.map((row) => {
          const {minimum, maximum} = row.version;
          const plannedVals = row.plannedValues.map((v) => v.value);
          const confirmedVals = row.confirmationValues.map((v) => v.value);
          const actualVals = row.actualValues.map((v) => v.value);
          return {
            key: row.version.id,
            cells: [
              {
                content: (
                  <Text fz="sm" key="name">
                    {row.version.name}
                  </Text>
                ),
              },
              // Setpoints define the target within the range, so they are never out of range.
              {content: buildCell({values: plannedVals, dataType: row.version.dataType})},
              buildDataParameterCell({
                parameterName: row.version.name,
                columnKind: 'confirmed',
                cellValues: confirmedVals,
                plannedValues: plannedVals,
                dataType: row.version.dataType,
                minimum,
                maximum,
                worksheetId,
              }),
              buildDataParameterCell({
                parameterName: row.version.name,
                columnKind: 'measured',
                cellValues: actualVals,
                plannedValues: plannedVals,
                dataType: row.version.dataType,
                minimum,
                maximum,
                worksheetId,
              }),
            ],
          };
        }),
      },
      {
        config: SECTION_CONFIG[1],
        columns: ['Material', 'Planned', 'Confirmed', 'Origin'],
        rows: parameterData.materialInputs.map((row) => ({
          key: row.version.id,
          cells: [
            {
              content: (
                <Text fz="sm" key="name">
                  {row.version.name}
                </Text>
              ),
            },
            {
              content: buildMaterialInputCell(
                row.plannedValues,
                row.version.dataType,
                row.version.amountUnit
              ),
            },
            buildMaterialInputConfirmedCell({
              parameterName: row.version.name,
              cellValues: row.confirmationValues,
              plannedValues: row.plannedValues,
              dataType: row.version.dataType,
              amountUnit: row.version.amountUnit,
              worksheetId,
            }),
            {content: renderMaterialLinkCell(row.version.id, originIndex, 'External', onNavigateToPmei)},
          ],
        })),
      },
      {
        config: SECTION_CONFIG[2],
        columns: ['Name', 'Entity', 'Downstream'],
        rows: parameterData.materialOutputs.flatMap((row) =>
          row.confirmationValues.map((cv) => ({
            key: cv.id,
            cells: [
              {
                content: (
                  <Text fz="sm" key="name">
                    {row.version.name}
                  </Text>
                ),
              },
              {content: buildCell({values: [cv.material], dataType: row.version.dataType})},
              {
                content: renderMaterialLinkCell(
                  row.version.id,
                  downstreamIndex,
                  'Terminal',
                  onNavigateToPmei
                ),
              },
            ],
          }))
        ),
      },
      {
        config: SECTION_CONFIG[3],
        columns: ['Equipment', 'Planned', 'Confirmed'],
        rows: parameterData.equipment.map((row) => ({
          key: row.version.id,
          cells: [
            {
              content: (
                <Text fz="sm" key="name">
                  {row.version.name}
                </Text>
              ),
            },
            {
              content: buildCell({
                values: row.plannedValues.map((v) => v.equipment),
                dataType: row.version.dataType,
              }),
            },
            buildEquipmentConfirmedCell({
              parameterName: row.version.name,
              cellValues: row.confirmationValues,
              plannedValues: row.plannedValues,
              dataType: row.version.dataType,
              worksheetId,
            }),
          ],
        })),
      },
    ],
    [parameterData, downstreamIndex, originIndex, onNavigateToPmei, worksheetId]
  );

  const hasAnySettings =
    parameterData.dataParameters.length > 0 ||
    parameterData.materialInputs.length > 0 ||
    // For material outputs, we only have confirmation values and we only want to show the material output section
    // if there are any material outputs with confirmation values. This is why when checking for material
    // outputs, we check if any of the material outputs have confirmation values.
    parameterData.materialOutputs.some((mo) => mo.confirmationValues.length > 0) ||
    parameterData.equipment.length > 0;

  if (!hasAnySettings) {
    return (
      <div {...getTestProps({component: 'PMEIParameterTables', element: 'root'})}>
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text="No parameters, material inputs, material outputs, or equipment are configured for this unit operation instance."
        />
      </div>
    );
  }

  return (
    <Stack spacing="md" {...getTestProps({component: 'PMEIParameterTables', element: 'root'})}>
      {sections.map(({config, columns, rows}) => (
        <ParameterSection
          key={config.key}
          sectionKey={config.key}
          title={config.title}
          icon={config.icon}
          columns={columns}
          rows={rows}
        />
      ))}
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  sectionCard: {
    border: `1px solid ${theme.colors.gray[2]}`,
    borderRadius: theme.radius.sm,
    backgroundColor: theme.white,
    overflow: 'hidden',
  },
  sectionTitle: {
    padding: `7px ${theme.spacing.md}`,
  },
  table: {
    borderTop: `1px solid ${theme.colors.gray[2]}`,
    // Equal-width columns that content cannot widen. Under `auto`, one long value outgrows its
    // siblings and `sectionCard`'s `overflow: hidden` clips the result.
    tableLayout: 'fixed',
    '& thead th': {
      backgroundColor: theme.colors.gray[0],
      fontWeight: 600,
      fontSize: theme.fontSizes.sm,
      borderBottom: 'none',
    },
    '& tbody td': {
      fontSize: theme.fontSizes.sm,
      borderTop: `1px solid ${theme.colors.gray[2]}`,
      verticalAlign: 'top',
      // Without this, an unbroken token (e.g. a name with no spaces) spills past its fixed column.
      overflowWrap: 'anywhere',
    },
    '& tbody tr': {
      transition: 'background-color 100ms ease',
    },
    // Highlight on hover, and on :focus-within so keyboard users tabbing into a row's
    // focusable content (e.g. entity chips/links) get the same visual row cue.
    '& tbody tr:hover, & tbody tr:focus-within': {
      backgroundColor: theme.colors.blue[0],
    },
  },
  // Backgrounds win here because `& tbody td` sets no backgroundColor. If a background is ever
  // added to `& tbody td`, nest these under `.table` so they keep winning on specificity.
  cellUnplanned: {backgroundColor: theme.colors.gray[1]},
  cellMismatch: {backgroundColor: theme.colors.yellow[2]},
  cellOutOfRange: {backgroundColor: theme.colors.orange[1]},
}));
